import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, onClick, qs, formDialog, toast, confirmDialog, type FieldSpec } from '../dom.js';
import { icon } from '../icons.js';
import { drainAssignment } from '../../testify/outbox.js';
import type { QuizRecord, TestifyExamRecord, IdentificationMode, ResultRecord } from '../../db/schema.js';

const MODE_BADGE: Partial<Record<IdentificationMode, string>> = { captured_name: 'no roster', lrn_direct: 'LRN direct' };

type SyncCounts = Record<NonNullable<ResultRecord['syncStatus']>, number>;

/** Compact per-class sync tally: synced / pending / rejected badges. */
function uploadBadges(c: SyncCounts): string {
  const parts: string[] = [];
  if (c.synced) parts.push(`<span class="badge badge-ok">${icon.check()} ${c.synced} uploaded</span>`);
  if (c.pending) parts.push(`<span class="badge badge-muted">${c.pending} queued</span>`);
  if (c.rejected) parts.push(`<span class="badge badge-danger">${icon.alert()} ${c.rejected} rejected</span>`);
  return parts.join(' ') || '—';
}

async function assignDialog(quiz: QuizRecord, exam: TestifyExamRecord | undefined, assignedClassIds: Set<string>) {
  const all = await repos.listClasses();
  if (!all.length) return toast('Create a class first', 'err');
  // One run per class: a class that already has an assignment isn't offered again,
  // so the picker can't produce a duplicate.
  const classes = all.filter((c) => !assignedClassIds.has(c.id));
  if (!classes.length) return toast('Every class is already assigned to this quiz', 'err');
  const today = new Date().toISOString().slice(0, 10);
  const cfFields: FieldSpec[] = (exam?.requiredCustomFields ?? [])
    .filter((f) => f.required)
    .map((f) => ({ name: 'cf_' + f.key, label: f.label + ' (required by TESTIFY)', required: true }));

  const v = await formDialog('Assign to a class', [
    { name: 'classId', label: 'Class', type: 'select', value: classes[0]?.id, options: classes.map((c) => ({ value: c.id, label: c.name })) },
    { name: 'date', label: 'Date administered', type: 'date', value: today },
    ...cfFields,
  ], 'Assign');
  if (!v) return;
  const customFields: Record<string, string> = {};
  for (const f of exam?.requiredCustomFields ?? []) if (v['cf_' + f.key] !== undefined) customFields[f.key] = v['cf_' + f.key];
  try {
    // Identity is always the bubbled LRN, auto-matched to the class roster.
    await repos.addAssignment(quiz.id, v.classId, v.date || today, 'roster', Object.keys(customFields).length ? customFields : undefined);
  } catch (e: any) {
    return toast(e.message, 'err');
  }
  toast('Class assigned');
  navigate(`#/quiz/${quiz.id}`);
}

const fmtDay = (ts: number) => new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** Detail card for an imported quiz: it is ONE subject of a unified TESTIFY exam. */
function testifyExamCard(quiz: QuizRecord, exam: TestifyExamRecord): string {
  const cf = (exam.requiredCustomFields ?? []).filter((f) => f.required);
  const kv = (label: string, value: string, mono = false) =>
    value ? `<div><dt>${esc(label)}</dt><dd${mono ? ' class="mono"' : ''}>${esc(value)}</dd></div>` : '';
  const sub = [exam.gradeLevel, exam.schoolYear ? `SY ${exam.schoolYear}` : ''].filter(Boolean).join(' · ');

  return `<div class="card"><div class="stack">
    <div><span class="badge badge-muted">${icon.doc()} Unified exam from TESTIFY</span></div>
    <div><strong>${esc(exam.examTitle || exam.examCode)}</strong>${sub ? `<span class="meta muted">${esc(sub)}</span>` : ''}</div>
    <dl class="kv">
      ${kv('Exam code', exam.examCode, true)}
      ${kv('School', exam.schoolName + (exam.schoolCode ? ` (${exam.schoolCode})` : ''))}
      ${kv('This subject', `${quiz.subject || quiz.title} · ${quiz.items} items${quiz.testId ? ` · test ${quiz.testId}` : ''}`)}
      ${kv('Compile version', `v${exam.compileVersion}`)}
      ${kv('Imported', fmtDay(exam.validatedAt))}
      ${cf.length ? `<div><dt>Required per class</dt><dd>${cf.map((f) => esc(f.label)).join(', ')}</dd></div>` : ''}
    </dl>
    <p class="hint">The answer key arrived locked from TESTIFY and can't be edited here. TESTIFY re-scores every upload server-side — scores in GRADIFY are advisory.</p>
  </div></div>`;
}

/**
 * The exam's OTHER subjects, as a jump list. Collapsed by default and placed
 * below the class actions: on a unified exam this can be dozens of rows, and it
 * is navigation, not information about THIS quiz — expanded at the top it pushed
 * the answer key, the TOS and "Assign to a class" off the first screen.
 */
function otherSubjectsBlock(quiz: QuizRecord, siblings: QuizRecord[]): string {
  const others = siblings.filter((s) => s.id !== quiz.id).sort((a, b) => a.title.localeCompare(b.title));
  if (!others.length) return '';
  return `<details class="card disclose" style="margin-top:22px">
    <summary>Other subjects in this exam (${others.length})</summary>
    <ul class="list">${others
      .map(
        (s) => `<button class="row" data-sibling="${esc(s.id)}"><span class="grow"><span class="ttl">${esc(s.title)}</span>
        <span class="meta">${s.items} items</span></span><span class="chev">${icon.chevron()}</span></button>`,
      )
      .join('')}</ul>
  </details>`;
}

export async function quizHubView(params: Record<string, string>): Promise<RenderedView> {
  const quiz = await repos.getQuiz(params.id);
  if (!quiz) return { title: 'Quiz', back: '/quizzes', html: `<div class="view"><p class="empty">Quiz not found.</p></div>` };
  const [key, map, assignments, classes] = await Promise.all([
    repos.answerKeyFor(quiz.id),
    repos.competencyMapFor(quiz.id),
    repos.assignmentsForQuiz(quiz.id),
    repos.listClasses(),
  ]);
  const exam = quiz.testifyExamId ? await repos.getTestifyExam(quiz.testifyExamId) : undefined;
  const siblings = exam ? await repos.quizzesForTestifyExam(exam.id) : [];
  const className = new Map(classes.map((c) => [c.id, c.name]));
  const results = await Promise.all(assignments.map((a) => repos.resultsForAssignment(a.id)));
  const scanned = results.reduce((n, r) => n + r.length, 0);
  const isTestify = quiz.origin === 'testify';
  // Per-class sync tallies drive the upload line on TESTIFY quizzes.
  const sync = isTestify ? await Promise.all(assignments.map((a) => repos.syncCounts(a.id))) : [];

  const locked = quiz.origin === 'testify' || !!key?.locked;
  const keyCount = key ? Object.keys(key.hashedAnswers ?? key.answers).length : 0;
  const keyBadge = locked
    ? `<span class="badge badge-muted">${icon.key()} Key from TESTIFY (locked · ${keyCount}/${quiz.items})</span>`
    : key
      ? `<span class="badge badge-ok">${icon.check()} Key set (${keyCount}/${quiz.items})</span>`
      : `<span class="badge badge-warn">${icon.alert()} No answer key</span>`;
  const keyBtn = locked
    ? `<button class="btn btn-ghost btn-block" data-key>${icon.key()} View key (locked)</button>`
    : `<button class="btn ${key ? 'btn-ghost' : 'btn-primary'} btn-block" data-key>${icon.key()} ${key ? 'Edit answer key' : 'Set answer key'}</button>`;

  // Table of Specifications: the item → competency map. Without it the app can
  // only report gaps per item; with it, per learning competency and per learner.
  const mapped = new Set((map?.competencies ?? []).flatMap((c) => c.items).filter((q) => q >= 1 && q <= quiz.items)).size;
  const compCount = map?.competencies.length ?? 0;
  const tosBadge = !compCount
    ? `<span class="badge badge-warn">${icon.alert()} No competency map (TOS)</span>`
    : mapped === quiz.items
      ? `<span class="badge badge-ok">${icon.check()} ${compCount} learning competencies · all ${quiz.items} items mapped</span>`
      : `<span class="badge badge-warn">${icon.alert()} ${compCount} learning competencies · ${mapped}/${quiz.items} items mapped</span>`;
  const tosCard = `<div class="card"><div class="stack">
    <div>${tosBadge}</div>
    ${compCount ? '' : `<p class="hint" style="margin:0">Add your Table of Specifications — each learning competency with the item numbers that measure it — to see which learners need remediation per competency.</p>`}
    <button class="btn ${compCount ? 'btn-ghost' : 'btn-primary'} btn-block" data-tos>${icon.list()} ${compCount ? 'Edit competencies (TOS)' : 'Add competencies (TOS)'}</button>
  </div></div>`;

  const asgCards = assignments
    .map((a, i) => {
      const res = results[i];
      const review = res.filter((r) => r.needsReview).length;
      const badge = MODE_BADGE[a.identificationMode ?? 'roster'];
      const c = sync[i];
      // Upload line: only for TESTIFY quizzes, only once something is queued.
      const queued = c ? c.pending + c.rejected : 0;
      const syncLine =
        isTestify && c && (queued || c.synced)
          ? `<div class="meta muted" id="syncmeta-${esc(a.id)}">${uploadBadges(c)}</div>
             ${queued ? `<button class="btn btn-ghost btn-sm btn-block" data-upload="${esc(a.id)}">${icon.upload()} Upload ${queued} to TESTIFY</button>` : ''}`
          : '';
      return `<div class="card"><div class="stack">
        <div><strong>${esc(className.get(a.classId) ?? 'Class')}</strong> <span class="muted">· ${esc(a.dateAdministered)}</span>${badge ? ` <span class="badge badge-muted">${esc(badge)}</span>` : ''}</div>
        <div class="meta muted">${res.length} scanned${review ? ` · <span class="danger-text">${review} to review</span>` : ''}</div>
        <div class="row2">
          <button class="btn btn-primary btn-sm" data-scan="${esc(a.id)}">${icon.camera()} Scan papers</button>
          <button class="btn btn-ghost btn-sm" data-review="${esc(a.id)}">${icon.list()} Review</button>
        </div>${syncLine}
        <button class="btn btn-danger btn-sm btn-block" data-remove="${esc(a.id)}">${icon.trash()} Remove this class</button></div></div>`;
    })
    .join('');

  return {
    title: quiz.title,
    subtitle: `${quiz.subject || '—'} · ${quiz.items} items${quiz.origin === 'testify' ? ' · TESTIFY' : ''}`,
    back: '/',
    html: `<div class="view">
      ${quiz.origin === 'testify'
        ? exam
          ? testifyExamCard(quiz, exam)
          : `<div class="card"><span class="badge badge-warn">${icon.alert()} Imported from TESTIFY — exam details missing</span></div>`
        : ''}
      <div class="card"><div class="stack"><div>${keyBadge}</div>${keyBtn}</div></div>
      ${tosCard}
      <p class="section-label">Classes (${assignments.length})</p>
      ${assignments.length ? asgCards : `<p class="hint">No classes assigned yet.</p>`}
      <button class="btn btn-ghost btn-block" data-assign style="margin-top:10px">${icon.plus()} Assign to a class</button>
      ${assignments.length ? `<button class="btn btn-primary btn-block" data-reports style="margin-top:10px">${icon.doc()} Print / Excel reports (all classes)</button>` : ''}
      ${assignments.length && compCount ? `<button class="btn btn-ghost btn-block" data-gaps style="margin-top:10px">${icon.chart()} Item/Competency Analysis (all classes)</button>` : ''}
      ${assignments.length ? `<button class="btn btn-ghost btn-block" data-analysis style="margin-top:10px">${icon.chart()} Item analysis &amp; CSV export (all classes)</button>` : ''}
      ${quiz.origin === 'testify' && exam ? otherSubjectsBlock(quiz, siblings) : ''}
      <p class="section-label" style="margin-top:22px">Danger zone</p>
      <button class="btn btn-danger btn-block" data-delete>${icon.trash()} Delete this quiz</button>
      <p class="hint">Removes the quiz, its answer key, ${assignments.length} class assignment${assignments.length === 1 ? '' : 's'} and ${scanned} scanned result${scanned === 1 ? '' : 's'}. This cannot be undone.</p>
    </div>`,
    mount(root) {
      onClick(root, '[data-key]', () => navigate(`#/quiz/${quiz.id}/key`));
      onClick(root, '[data-scan]', (el) => navigate(`#/assignment/${el.dataset.scan}/scan`));
      onClick(root, '[data-review]', (el) => navigate(`#/assignment/${el.dataset.review}/review`));
      onClick(root, '[data-analysis]', () => navigate(`#/quiz/${quiz.id}/analysis`));
      onClick(root, '[data-tos]', () => navigate(`#/quiz/${quiz.id}/competencies`));
      onClick(root, '[data-gaps]', () => navigate(`#/quiz/${quiz.id}/gaps`));
      onClick(root, '[data-reports]', () => navigate(`#/quiz/${quiz.id}/reports`));
      onClick(root, '[data-assign]', () => assignDialog(quiz, exam, new Set(assignments.map((a) => a.classId))));
      onClick(root, '[data-remove]', async (el) => {
        const asgId = el.dataset.remove!;
        const a = assignments.find((x) => x.id === asgId);
        if (!a) return;
        const name = className.get(a.classId) ?? 'this class';
        const impact = await repos.assignmentDeletionImpact(asgId);
        if (!confirmDialog(`Remove ${name} from "${quiz.title}"?${impact.results ? ` Its ${impact.results} scanned result${impact.results === 1 ? '' : 's'} are deleted too.` : ''} The quiz and its answer key stay. This cannot be undone.`)) return;
        // Scanned papers are the only record of a sitting — make losing them deliberate.
        if (impact.results && !confirmDialog(`Really delete ${impact.results} scanned result${impact.results === 1 ? '' : 's'} for ${name}?${impact.synced ? ` ${impact.synced} already uploaded to TESTIFY — removing them here does NOT undo that upload.` : ' Export the analysis first if you still need the scores.'}`)) return;
        await repos.deleteAssignment(asgId);
        toast('Class removed');
        navigate(`#/quiz/${quiz.id}`);
      });
      onClick(root, '[data-upload]', async (el) => {
        const asgId = el.dataset.upload!;
        const assignment = assignments.find((a) => a.id === asgId);
        if (!assignment) return;
        const btn = el as HTMLButtonElement;
        btn.disabled = true;
        const original = btn.innerHTML;
        btn.innerHTML = `${icon.upload()} Uploading…`;
        try {
          const sum = await drainAssignment(repos, assignment, (done, total) => {
            btn.innerHTML = `${icon.upload()} Uploading ${done}/${total}…`;
          });
          // Refresh the tally line in place, then swap the button for the result.
          const meta = qs(root, `#syncmeta-${CSS.escape(asgId)}`);
          if (meta) meta.innerHTML = uploadBadges(await repos.syncCounts(asgId));
          if (sum.haltReason) {
            toast(sum.haltReason, 'err', 5000);
            btn.disabled = false;
            btn.innerHTML = original;
          } else if (sum.rejected) {
            toast(`${sum.synced} uploaded · ${sum.rejected} rejected — see review`, 'err', 4000);
            btn.disabled = false;
            btn.innerHTML = `${icon.upload()} Retry ${sum.rejected} rejected`;
          } else if (sum.skippedNotMappable && !sum.attempted) {
            // The stored key predates upload support — re-importing rebuilds it.
            toast('This exam was imported before uploads were supported — re-import it from TESTIFY, then upload.', 'err', 6000);
            btn.disabled = false;
            btn.innerHTML = original;
          } else if (sum.skippedNoLrn && !sum.attempted) {
            toast(`${sum.skippedNoLrn} result(s) have no clear 12-digit LRN — open Review and fix the LRN first.`, 'err', 5000);
            btn.disabled = false;
            btn.innerHTML = original;
          } else if (sum.skipped && !sum.attempted) {
            toast(`${sum.skipped} result(s) could not be uploaded.`, 'err', 4000);
            btn.disabled = false;
            btn.innerHTML = original;
          } else if (sum.skipped) {
            // Some went up, some still need an LRN — keep the button for the rest.
            toast(`${sum.synced} uploaded · ${sum.skipped} still need a valid LRN`, 'err', 4500);
            btn.disabled = false;
            btn.innerHTML = `${icon.upload()} Upload ${sum.skipped} remaining`;
          } else {
            toast(`${sum.synced} uploaded to TESTIFY`);
            btn.remove();
          }
        } catch (e) {
          toast(e instanceof Error ? e.message : 'Upload failed', 'err');
          btn.disabled = false;
          btn.innerHTML = original;
        }
      });
      onClick(root, '[data-sibling]', (el) => navigate(`#/quiz/${el.dataset.sibling}`));
      onClick(root, '[data-delete]', async () => {
        if (!confirmDialog(`Delete "${quiz.title}"? Its answer key, ${assignments.length} class assignment${assignments.length === 1 ? '' : 's'} and ${scanned} scanned result${scanned === 1 ? '' : 's'} are deleted too. This cannot be undone.`)) return;
        // Scanned papers are the only record of a sitting — make losing them deliberate.
        if (scanned && !confirmDialog(`Really delete ${scanned} scanned result${scanned === 1 ? '' : 's'}? Export the analysis first if you still need the scores.`)) return;
        await repos.deleteQuiz(quiz.id);
        toast('Quiz deleted');
        navigate('#/quizzes');
      });
    },
  };
}
